/** The two shapes every tray panel is built from: a named block, and a read with three answers. */

import type { ReactNode } from "react";

import { EmptyState, ErrorState, Skeleton, Stack } from "@/components/ui";

import type { PanelQuery } from "../../account/components/QueryPanel";
import { clockStamp, errorText } from "../../account/lib/present";
import { Muted } from "../../components/Text";

export function Section({
  title,
  action,
  note,
  children,
}: {
  readonly title: string;
  readonly action?: ReactNode;
  /** One short line under the heading. Names a fact, never explains one. */
  readonly note?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="cx-tray-section">
      <div className="cx-tray-head">
        <h4 className="cx-tray-title">{title}</h4>
        {action}
      </div>
      {note === undefined ? null : <Muted small>{note}</Muted>}
      {children}
    </section>
  );
}

/**
 * One read, three answers: a skeleton at its own shape, a named failure, or the data. A read
 * that failed but still holds a value renders it with the instant it was read, never bare.
 */
export function Loaded<T>({
  query,
  rows,
  children,
}: {
  readonly query: PanelQuery<T>;
  readonly rows?: number;
  readonly children: (data: T) => ReactNode;
}) {
  if (query.isPending) {
    return (
      <Stack gap={2}>
        {Array.from({ length: rows ?? 2 }, (_unused, index) => (
          <Skeleton key={index} height="28px" />
        ))}
      </Stack>
    );
  }

  if (query.data === undefined) {
    return (
      <ErrorState
        headline="Read failed"
        code={errorText(query.error)}
        retryLabel="Retry"
        onRetry={query.refetch}
      />
    );
  }

  return (
    <Stack gap={2}>
      {query.isError ? <Muted small>{`read at ${clockStamp(query.dataUpdatedAt)}`}</Muted> : null}
      {children(query.data)}
    </Stack>
  );
}

/** Nothing to show, and whether that is ordinary or a dead end with a way out. */
export function Nothing({
  headline,
  action,
}: {
  readonly headline: string;
  readonly action?: ReactNode;
}) {
  return <EmptyState bare headline={headline} {...(action === undefined ? {} : { action })} />;
}
